import { Article } from '../types';

export const electricityBillingDisputeResolution: Article = {
    id: 'electricity-billing-dispute-resolution',
    slug: 'electricity-billing-dispute-resolution',
    title: 'Electricity Billing Dispute Resolution in Nigeria',
    excerpt: 'Step-by-step guide to disputing estimated bills, overbilling, wrong tariff bands, meter errors, and unresolved DisCo complaints in Nigeria.',
    category: 'Power Generation',
    date: 'January 28, 2026',
    dateModified: 'June 8, 2026',
    readTime: '7 min read',
    author: 'Consumer Rights Team',
    trending: true,
    keywords: ['electricity billing dispute Nigeria', 'estimated billing complaint', 'NERC complaint', 'DisCo overbilling', 'wrong tariff band'],
    metaDescription: 'Resolve electricity billing disputes in Nigeria: gather evidence, contact your DisCo, escalate to NERC, challenge estimated bills, and protect yourself from overbilling.',
    faqs: [
        {
            question: 'How do I dispute an electricity bill in Nigeria?',
            answer: 'Document your meter reading, bills, payments, supply hours, and complaint number. First report to your DisCo customer care, then escalate to the NERC Forum if unresolved within the allowed complaint period.',
        },
        {
            question: 'Can I dispute estimated billing?',
            answer: 'Yes. Ask your DisCo for the billing basis, submit evidence of consumption or supply hours, request metering, and escalate if the estimate is unreasonable.',
        },
        {
            question: 'What evidence helps in a billing dispute?',
            answer: 'Useful evidence includes meter photos, old bills, payment receipts, outage logs, feeder/band information, complaint tickets, and screenshots of customer-care conversations.',
        },
    ],
    content: `Electricity billing disputes are common in Nigeria, especially for customers facing estimated billing, wrong tariff bands, prepaid meter errors, uncredited payments, or sudden "crazy bills." The fastest way to resolve a dispute is to treat it like a case file: gather evidence, get a complaint number, and escalate in the right order.

## Quick Answer

To dispute an electricity bill, first gather your meter reading, old bills, payment receipts, supply-hour log, and account number. Report the issue to your Distribution Company (DisCo) and insist on a ticket number. If the complaint is not resolved, escalate to the NERC Forum Office with your evidence.

## Common Billing Problems

### 1. Estimated Billing

Estimated billing happens when a customer is not metered or the meter is not read. The problem is that estimates can be higher than actual consumption, especially during long outages.

### 2. Wrong Tariff Band

If you are charged as Band A but your feeder does not deliver the required hours, document supply hours and ask for a tariff-band review.

### 3. Prepaid Meter Token Issues

If payment was successful but the token failed, save the transaction receipt and meter error message. You may need a Key Change Token (KCT), meter activation, or vendor correction.

### 4. Uncredited Payments

For bank/app payments that do not reflect, screenshot the transaction reference and ask your DisCo or payment provider to trace it.

## Evidence to Gather

- Account number and meter number
- Photos of the meter reading and meter serial number
- Copies of the disputed bill and previous bills
- Payment receipts and transaction references
- Outage or supply-hour log for at least 7 days
- Screenshots of emails, WhatsApp chats, or app complaints
- Complaint ticket number from the DisCo

## Step-by-Step Dispute Process

### Step 1: Report to Your DisCo

Use official customer-care channels. Explain the issue clearly and request a complaint ticket number.

### Step 2: Ask for the Billing Basis

For estimated bills, ask how the estimate was calculated. For tariff issues, ask for your feeder, band, and approved rate.

### Step 3: Submit Evidence

Attach meter photos, payment evidence, supply-hour logs, or token error screenshots. Keep copies of everything.

### Step 4: Follow Up in Writing

If you speak by phone, follow up by email or written complaint so there is a record.

### Step 5: Escalate to NERC

If the DisCo does not resolve the issue, escalate to the NERC Forum Office in your area. Include your account number, complaint ticket, evidence, and the remedy you want.

## Sample Complaint Wording

Subject: Billing dispute on account [ACCOUNT NUMBER]

I am disputing the electricity bill dated [DATE] because [short reason]. My evidence includes [meter reading/payment receipt/supply log]. I request a bill review, correction of the account, and written explanation of the billing basis.

## What to Ask For

- Bill correction or reversal
- Payment tracing
- Tariff-band review
- Meter inspection
- Prepaid meter installation
- Written explanation of charges

## When Solar May Be the Better Answer

If you repeatedly spend money on poor supply, generator fuel, and unresolved billing disputes, compare the cost of solar backup. A properly sized solar system can reduce dependence on both estimated billing and generator fuel.`
};
